import {
  useState,
  useEffect,
  FunctionComponent,
  Dispatch,
  SetStateAction,
  PropsWithoutRef,
  useContext,
  ReducerWithoutAction,
} from "react";
import { useReactFlow } from "reactflow";
import styles from "./styles.module.scss";
import classNames from "classnames";
import { Collapse, Table as UITable } from "../../../shared";
import { CLL } from "../cllUtils";
import {
  bfsTraversal,
  expandTableLineage,
  layoutElementsOnCanvas,
  removeColumnNodes,
  resetTableHighlights,
  setHighlightedTableConnections,
} from "../graph";

// ui components
import { ComponentLoader } from "./Loader";
import { ColumnDatatype, NodeIcon } from "./Column";
import { ColorTag } from "./Tags";
import ChatStarterButton from "./ChatStarter";

// assets
import { ExpandLineageIcon } from "../assets/icons";
import { CustomInput } from "./Form";
import { defaultEdgeStyle, isNotColumn, toggleColumnEdges, toggleModelEdges } from "../utils";
import PurposeSection from "./Purpose";
import {
  Column,
  ColumnLineage,
  Columns,
  Confidence,
  Table,
  CollectColumn,
  DbtInfo,
} from "../types";
import { getColumns, getDbtInfo, previewFeature, showInfoNotification } from "../api";
import { LineageContext, useLineageContext, useLineageDispatch } from "../context/LineageProvider";
import {
  setSelectedColumn,
  setCollectColumns,
  setSidebarScreen,
  setConfidence,
  updateConfidenceWithOperatorList,
  mergeSeeMoreTables,
  mergeCollectColumns,
  setErrors,
  setModalArgs,
} from "../context/LineageSlice";
import { CodeBlock, Stack, TooltipCore, TooltipTrigger, TooltipContent } from "@altimateai/lego";
import { LineageType } from "../context/types";
import {
  BoltIcon,
  BracketsIcon,
  CaseSensitiveIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DbtIcon,
  HalfCircleIcon,
  SplitHorizontalIcon,
} from "../../../assets/icons";
import { IconShare3 } from "@tabler/icons-react";
import { Button, Card, CardContent } from "@altimateai/lego";
import ViewsTypeBadge from "./ViewsTypeBadge";

interface TableData {
  [key: string]: string | number | boolean | null | undefined;
}

interface LightDashNode {
  type: string;
  name: string;
  description?: string;
  meta?: Record<string, unknown>;
  sql?: string;
  time_intervals?: string[];
  hidden?: boolean;
  round?: boolean;
  format?: string;
  compact?: boolean;
  groups?: string[];
  urls?: string[];
  required_attributes?: string[];
  colors?: string[];
  joins?: unknown[];
}

export const ColumnCard: FunctionComponent<{
  column: Column & { code?: string };
  handleClick: () => void;
  selected: boolean;
  isSelectable: boolean;
}> = ({ column, handleClick, selected, isSelectable }) => {
  const {
    state: { theme, config, renderTags, client },
    dispatch,
  } = useContext(LineageContext);
  return (
    <div
      className={classNames(styles.column_card, {
        [styles.selected]: selected,
        "cursor-pointer": isSelectable,
      })}
      onClick={handleClick}
      data-testid={"table-details-" + column.name}
    >
      <Stack align="center" gap="xs">
        <ColumnDatatype datatype={column.datatype || ""} />
        <div className="lines-2">{column.name}</div>
        <div className="spacer" />
        {client === "extension" && (
          <span className={styles.col_chat_action}>
            <ChatStarterButton
              level="column"
              table={column.table}
              column={column.name}
              tooltip="Chat about this column"
            />
          </span>
        )}
        {column.is_transformation && <ViewsTypeBadge viewsType={"Transformation"} />}
        {/* Using TooltipCore primitives instead of IconButton/Tooltip because
            the sidebar renders at z-index ~998, and the default Tooltip z-index (50)
            causes it to appear behind the sidebar. TooltipContent with al-z-[1000]
            ensures the tooltip renders above the sidebar. */}
        {config.exportFinalLineage && (
          <TooltipCore>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                title=""
                variant="ghost"
                className="al-w-8 al-h-8"
                aria-label="Export column lineage as CSV"
                onClick={async (e: React.MouseEvent) => {
                  e.stopPropagation();
                  dispatch(
                    setModalArgs({
                      type: "export_final_lineage",
                      args: { table: column.table, column: column.name },
                    })
                  );
                }}
              >
                <IconShare3 className="al-w-4 al-h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="al-z-[1000]">Export column lineage as CSV</TooltipContent>
          </TooltipCore>
        )}
        {column.can_lineage_expand && (
          <div className={styles.expand_lineage_icon}>
            <ExpandLineageIcon />
          </div>
        )}
        {column.datatype && <ColorTag label={column.datatype} />}
      </Stack>
      {column.description && (
        <Stack direction="column">
          <div className="al-font-normal al-text-xs al-text-gray-500">{column.description}</div>
        </Stack>
      )}
      {renderTags && renderTags({ column, table_rk: column.table })}
      {column.code && (
        <CodeBlock code={column.code} language="sql" editorTheme="tomorrow" theme={theme} />
      )}
    </div>
  );
};

const ColumnSection: FunctionComponent<{
  columns: Column[];
  filteredColumn: Column[];
  setFilteredColumn: Dispatch<SetStateAction<Column[]>>;
  handleColumnClick: (_x: Column) => Promise<void>;
  selectedTable: Table | null;
  selectedColumn?: { name: string; table: string };
  setData: Dispatch<SetStateAction<Columns | null>>;
  allowSyncColumnsWithDB?: boolean;
  lineageType: LineageType;
}> = ({
  columns,
  filteredColumn,
  setFilteredColumn,
  handleColumnClick,
  selectedTable,
  selectedColumn,
  setData,
  allowSyncColumnsWithDB,
  lineageType,
}) => {
  const isEphemeral = selectedTable?.materialization === "ephemeral";
  const isAnalysis = selectedTable?.nodeType === "analysis";
  return (
    <div className={classNames(styles.card, "al-flex-grow column-section")}>
      <Stack direction="column" gap="md" className="al-h-full al-p-2">
        <Stack align="center" gap="xs">
          <div className="al-text-xl al-font-semibold">Columns</div>
          <div className="spacer" />
          {allowSyncColumnsWithDB && !isEphemeral && !isAnalysis && (
            <Button
              onClick={() => {
                if (!selectedTable) return;
                getColumns(selectedTable.table, true).then(_data => {
                  if (_data) {
                    setData(_data);
                    setFilteredColumn(_data.columns);
                  }
                });
              }}
            >
              Sync with DB
            </Button>
          )}
        </Stack>
        <CustomInput
          type="text"
          placeholder="Search by column name"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const _search = e.target.value.toLowerCase();
            setFilteredColumn(columns.filter(c => c.name.toLowerCase().includes(_search)));
          }}
        />
        <Stack align="center" gap="xs">
          {!isEphemeral && <div className="al-text-xs">Select column for lineage</div>}
          <div className="spacer" />
          <div className="al-text-xs al-text-gray-500">{filteredColumn.length} columns</div>
        </Stack>
        <Stack direction="column" gap="md">
          {filteredColumn.map(_column => (
            <ColumnCard
              key={_column.name}
              column={_column}
              handleClick={() => {
                if (isEphemeral) return;
                handleColumnClick(_column);
              }}
              selected={
                _column.name === selectedColumn?.name && _column.table === selectedColumn?.table
              }
              isSelectable={!isEphemeral && lineageType === "dynamic"}
            />
          ))}
        </Stack>
      </Stack>
    </div>
  );
};

const TestSection: FunctionComponent<{
  tests: { key: string; path: string }[];
}> = ({ tests }) => {
  const [filteredTests, setFilteredTests] = useState(tests);
  return (
    <div className={classNames(styles.card, "al-flex-grow column-section")}>
      <Stack direction="column" gap="md" className="al-h-full al-p-2">
        <div className="al-text-xl al-font-semibold">Tests</div>
        <CustomInput
          type="text"
          placeholder="Search by test"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const _search = e.target.value.toLowerCase();
            setFilteredTests(tests.filter(t => t.key.toLowerCase().includes(_search)));
          }}
        />
        <Stack align="center" gap="xs">
          <div className="al-text-xs al-text-gray-500">{filteredTests.length} tests</div>
        </Stack>
        <Stack direction="column" gap="md">
          {filteredTests.map(_test => (
            <div key={_test.key} className={styles.column_card}>
              <Stack align="center" gap="xs">
                <div className="lines-2">{_test.key}</div>
              </Stack>
            </div>
          ))}
        </Stack>
      </Stack>
    </div>
  );
};

const icons = [BracketsIcon, HalfCircleIcon, CaseSensitiveIcon, SplitHorizontalIcon];

const getIconForType = (type: string): string => {
  switch (type) {
    case "dimension":
      return BracketsIcon;
    case "Order Type":
      return HalfCircleIcon;
    case "metrics":
      return CaseSensitiveIcon;
    case "table":
      return SplitHorizontalIcon;
    default:
      return icons[Math.floor(Math.random() * icons.length)];
  }
};

const tableMetaKeys = [
  "label",
  "type",
  "description",
  "sql",
  "time_intervals",
  "hidden",
  "round",
  "format",
  "compact",
  "groups",
  "urls",
  "required_attributes",
  "colors",
  "joins",
];

const DataTable = ({ data }: { data: TableData }) => {
  return (
    <UITable>
      <tbody>
        {Object.entries(data).map(([key, value]) => (
          <tr key={key}>
            <td>{key}</td>
            <td>{String(value)}</td>
          </tr>
        ))}
      </tbody>
    </UITable>
  );
};

const renderValue = (value: unknown): JSX.Element | string => {
  if (typeof value === "object" && value !== null) {
    return <DataTable data={value as TableData} />;
  }
  return String(value);
};

export const LightDashSection: FunctionComponent<{ node: LightDashNode }> = ({ node }) => {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  const toggleCollapse = (key: string) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Stack direction="column" gap="md">
      {tableMetaKeys.map(key => {
        const value = node[key as keyof LightDashNode];
        if (!value) return null;

        return (
          <Card key={key}>
            <CardContent className="al-p-2 al-pt-2 al-pb-2">
              <div className="al-cursor-pointer" onClick={() => toggleCollapse(key)}>
                <Stack align="center" gap="xs">
                  <NodeIcon nodeType={getIconForType(key)} />
                  <div className="al-capitalize">{key}</div>
                  <div className="spacer" />
                  {openSections[key] ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </Stack>
              </div>
              <Collapse isOpen={openSections[key]}>
                <div className="al-mt-2">{renderValue(value)}</div>
              </Collapse>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  );
};

export const HeaderSection = ({
  nodeType,
  table,
  tableData,
  currentTableId,
  onNavigate,
}: PropsWithoutRef<{
  nodeType: string;
  table: string;
  tableData?: Table;
  currentTableId?: string;
  onNavigate?: (_navigationInfo: { url?: string; id?: string; table: string }) => void;
}>) => {
  const {
    state: { client, config },
  } = useLineageContext();
  const dispatch = useLineageDispatch();

  // Check if we should show the button based on provided currentTableId and client
  const shouldShowButton = () => {
    // Only show button in SaaS client (not in extension client)
    if (client !== "saas") return false;

    if (!tableData?.id && !tableData?.url) return false;
    if (tableData?.url) return true;

    // Don't show the button if the current ID is the same as tableData.id
    return currentTableId !== tableData.id;
  };

  const handleNavigateClick = () => {
    if (!onNavigate || !tableData) return;

    onNavigate({
      url: tableData.url as string | undefined,
      id: tableData.id,
      table: tableData.table,
    });
  };

  return (
    <div className={styles.table_details_header}>
      <NodeIcon nodeType={nodeType} />
      <Stack align="center">
        <div className="al-font-semibold al-text-xl lines-2">{table}</div>
        <div className="spacer" />
        {/* See ColumnCard for why TooltipCore is used here */}
        {config.exportFinalLineage && tableData?.table && (
          <TooltipCore>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                title=""
                variant="ghost"
                className="al-w-8 al-h-8"
                aria-label="Export table lineage as CSV"
                onClick={() => {
                  dispatch(
                    setModalArgs({
                      type: "export_final_table_lineage",
                      args: { table: tableData.table },
                    })
                  );
                }}
              >
                <IconShare3 className="al-w-4 al-h-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="al-z-[1000]">Export table lineage as CSV</TooltipContent>
          </TooltipCore>
        )}
        {(tableData?.url || tableData?.id) && shouldShowButton() && (
          <Button size="sm" onClick={handleNavigateClick}>
            View Details
          </Button>
        )}
      </Stack>
    </div>
  );
};

const TableDetails = ({
  lineageType,
  currentTableId,
  onNavigate,
}: {
  lineageType: LineageType;
  currentTableId?: string;
  onNavigate?: (navigationInfo: { url?: string; id?: string; table: string }) => void;
}) => {
  const {
    rerender,
    state: {
      selectedTable,
      selectedColumn,
      selectCheck,
      nonSelectCheck,
      aiEnabled,
      allowSyncColumnsWithDB,
      errors,
      theme,
      lightdashEnabled,
    },
  } = useLineageContext();
  const lineageDispatch = useLineageDispatch();

  const flow = useReactFlow();
  const [filteredColumn, setFilteredColumn] = useState<Column[]>([]);
  const [data, setData] = useState<Columns | null>(null);
  const [dbtInfo, setDbtInfo] = useState<DbtInfo | null>(null);
  const [tab, setTab] = useState(0);

  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (!selectedTable) return;
    setIsLoading(true);
    getColumns(selectedTable, false)
      .then(_data => {
        setData(_data);
        setFilteredColumn(_data.columns);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
    getDbtInfo(selectedTable)
      .then(_data => {
        setDbtInfo(_data.dbt_info);
        setIsLoading(false);
      })
      .catch(() => {
        setDbtInfo(null);
        setIsLoading(false);
      });
  }, [selectedTable]);

  const handleColumnClick = async (_column: Column) => {
    if (!aiEnabled) {
      previewFeature();
      return;
    }

    if (CLL.inProgress) {
      CLL.showCllInProgressMsg();
      return;
    }

    // remove column selection when already selected
    if (selectedColumn?.table === _column.table && selectedColumn?.name === _column.name) {
      const [_nodes, _edges] = removeColumnNodes(flow.getNodes(), flow.getEdges());
      // Model edges will be hidden when column lineage is selected, so unhide them
      toggleModelEdges(_edges, true);
      toggleColumnEdges(_edges, true);

      flow.setNodes(_nodes);
      flow.setEdges(_edges);
      lineageDispatch(setSelectedColumn({ table: "", name: "" }));
      lineageDispatch(setCollectColumns({}));
      lineageDispatch(setSidebarScreen(""));
      return;
    }

    const tableNodeData = flow.getNode(_column.table)?.data;
    if (!tableNodeData) {
      throw new Error(`table node ${_column.table} isn't visible`);
    }

    // expand 1st level if not already
    let _nodes = flow.getNodes();
    let _edges = flow.getEdges();
    // Model edges should be hidden when column lineage is selected
    toggleModelEdges(_edges, false);
    toggleColumnEdges(_edges, true);

    const addNodesEdges = async (right: boolean) => {
      [_nodes, _edges] = await expandTableLineage(_nodes, _edges, _column.table, right);
      layoutElementsOnCanvas(_nodes, _edges);
    };
    const { upstreamCount, downstreamCount } = tableNodeData;
    if (upstreamCount > 0 && _edges.filter(e => e.source === _column.table).length < upstreamCount)
      await addNodesEdges(true);
    if (
      downstreamCount > 0 &&
      _edges.filter(e => e.target === _column.table).length < downstreamCount
    )
      await addNodesEdges(false);

    // initializing states
    lineageDispatch(setSelectedColumn({ ..._column }));
    lineageDispatch(setSidebarScreen(""));
    lineageDispatch(setCollectColumns({}));
    lineageDispatch(setConfidence({ confidence: "high" }));

    // resetting canvas
    const [nodes, edges] = resetTableHighlights(
      _nodes.filter(isNotColumn),
      _edges.filter(isNotColumn)
    );
    edges.forEach(_e => (_e.style = defaultEdgeStyle));
    flow.setNodes(nodes);
    flow.setEdges(edges);
    rerender();

    // starting column lineage
    const _bfsTraversal = (right: boolean) =>
      bfsTraversal(
        nodes,
        edges,
        right,
        [_column],
        (operatorList: Confidence["operator_list"]) => {
          lineageDispatch(updateConfidenceWithOperatorList({ operatorList }));
        },
        (moreTables: ColumnLineage[]) => {
          lineageDispatch(mergeSeeMoreTables(moreTables));
        },
        (newCollectColumns: Record<string, CollectColumn[]>) => {
          lineageDispatch(mergeCollectColumns(newCollectColumns));
        },
        () => [flow.getNodes(), flow.getEdges()],
        (ns, es) => {
          flow.setNodes(ns);
          flow.setEdges(es);
        },
        _column,
        { direct: selectCheck, indirect: nonSelectCheck },
        (fn: ReducerWithoutAction<Record<string, string[]>>) =>
          lineageDispatch(setErrors(fn(errors)))
      );
    try {
      CLL.start();
      const result = await Promise.all([_bfsTraversal(true), _bfsTraversal(false)]);
      if (result.every(isLineage => !isLineage)) {
        lineageDispatch(setSelectedColumn({ table: "", name: "" }));
        setHighlightedTableConnections(flow, _column.table);
        if (!CLL.isCancelled) {
          showInfoNotification(
            `No lineage found for model ${_column.table} and column ${_column.name}`
          );
        }
      }
    } catch (e) {
      console.error("Error while performing cll for ", _column.table, _column.name, ", error:", e);
      lineageDispatch(setSelectedColumn({ table: "", name: "" }));
      setHighlightedTableConnections(flow, _column.table);
    } finally {
      CLL.end();
    }
  };
  const selectedTableData = flow.getNode(selectedTable)?.data;
  if (isLoading || !data || !selectedTable) return <ComponentLoader />;
  const tabs = ["Column"];
  if (selectedTableData.tests.length) tabs.push("Tests");
  if (lightdashEnabled) {
    tabs.push("Lightdash");
  }
  if (dbtInfo !== null) {
    tabs.push("dbt");
  }

  return (
    <Stack direction="column" gap="2xl" className="al-p-2 al-h-full al-overflow-y-auto">
      <HeaderSection
        nodeType={selectedTableData.nodeType}
        table={selectedTableData.label}
        tableData={selectedTableData}
        currentTableId={currentTableId}
        onNavigate={onNavigate}
      />
      {data.purpose && <PurposeSection purpose={data.purpose} />}
      {tabs.length > 1 && (
        <div className={styles.table_details_tabs}>
          {tabs.map((label, i) => (
            <div
              key={label}
              className={classNames(styles.tab, {
                [styles.selected]: tab === i,
              })}
              onClick={() => setTab(i)}
            >
              <Stack align="center" justify="center" gap="md">
                <div
                  className={theme === "light" && tab !== i ? "al-text-primary" : "al-text-white"}
                >
                  {label === "Lightdash" ? <BoltIcon /> : null}
                  {label === "dbt" ? <DbtIcon className={"al-w-4 al-h-4"} /> : null}
                </div>
                {label}
              </Stack>
            </div>
          ))}
        </div>
      )}
      {tabs[tab] === "Column" && (
        <ColumnSection
          selectedTable={selectedTableData}
          selectedColumn={selectedColumn}
          filteredColumn={filteredColumn}
          setFilteredColumn={setFilteredColumn}
          columns={data.columns}
          handleColumnClick={handleColumnClick}
          setData={setData}
          allowSyncColumnsWithDB={allowSyncColumnsWithDB}
          lineageType={lineageType}
        />
      )}
      {tabs[tab] === "Tests" && <TestSection tests={selectedTableData.tests} />}
      {tabs[tab] === "Lightdash" && <LightDashSection node={selectedTableData} />}
      {tabs[tab] === "dbt" && <Stack align={"center"}>{renderValue(dbtInfo)}</Stack>}
    </Stack>
  );
};

export const StaticTableDetails = ({
  currentTableId,
  onNavigate,
}: {
  currentTableId?: string;
  onNavigate?: (navigationInfo: { url?: string; id?: string; table: string }) => void;
} = {}) => {
  const {
    state: { theme },
  } = useContext(LineageContext);
  const {
    state: { sqlLineage, selectedTable, allowSyncColumnsWithDB },
  } = useLineageContext();

  const _columns = sqlLineage?.details?.[selectedTable]?.columns?.map(item => ({
    ...item,
    description: item.expression,
  }));
  const [columns, setColumns] = useState(_columns);
  const [filteredColumn, setFilteredColumn] = useState(_columns);
  const table = sqlLineage?.details?.[selectedTable];
  if (!table) {
    return null;
  }
  const { sql, type, nodeId } = table;

  // Create a tableData object with available information
  const tableData = {
    table: selectedTable,
    label: selectedTable,
    nodeType: type || "cte",
    downstreamCount: 0,
    upstreamCount: 0,
    isExternalProject: false,
    tests: [],
    nodeId,
  };

  return (
    <Stack direction="column" gap="2xl" className="al-p-2 al-h-full al-overflow-y-auto">
      <HeaderSection
        nodeType={type || "cte"}
        table={selectedTable}
        tableData={tableData as Table}
        currentTableId={currentTableId}
        onNavigate={onNavigate}
      />
      {sql && (
        <div className={classNames(styles.card, "al-mb-0 purpose-section")}>
          <Stack direction="column" gap="md">
            <div className="al-text-xl al-font-semibold">SQL</div>
            <CodeBlock
              code={sql}
              language="sql"
              editorTheme="tomorrow"
              showLineNumbers
              theme={theme}
            />
          </Stack>
        </div>
      )}
      <div className={classNames(styles.card, "al-mb-0 al-flex-grow column-section")}>
        <Stack direction="column" gap="md" className="al-h-full">
          <Stack align="center" gap="xs">
            <div className="al-text-xl al-font-semibold">Column</div>
            <div className="spacer" />
            {allowSyncColumnsWithDB && nodeId && ["table", "final"].includes(type || "") && (
              <Button
                onClick={() => {
                  getColumns(nodeId, true).then(_data => {
                    setColumns(_data.columns);
                    setFilteredColumn(_data.columns);
                  });
                }}
              >
                Sync with DB
              </Button>
            )}
          </Stack>
          <CustomInput
            placeholder="Search by column name"
            type="text"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const _search = e.target.value.toLowerCase();
              setFilteredColumn(columns?.filter(c => c.name.toLowerCase().includes(_search)));
            }}
          />
          <Stack align="center" gap="xs">
            <div>{filteredColumn?.length} columns</div>
          </Stack>
          <Stack direction="column" gap="md" className="al-overflow-y-auto">
            {filteredColumn?.map(_column => (
              <ColumnCard
                key={_column.name}
                column={{
                  name: _column.name,
                  table: selectedTable,
                  can_lineage_expand: false,
                  description: "",
                  code: _column.expression,
                }}
                handleClick={() => {}}
                selected={false}
                isSelectable={false}
              />
            ))}
          </Stack>
        </Stack>
      </div>
    </Stack>
  );
};

export { TableDetails };
